import React, { useEffect, useMemo, useRef, useState } from "react";
import { doc, setDoc, addDoc, collection, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import { colors, radius, spacing, typography, cardBase } from "../../utils/designTokens";
import { useIsMobile } from "../../hooks/useResponsive";
import { useUser } from "../../context/UserContext";
import { useToast } from "../../components/Toast";
import { DEFAULT_PUNTOS, buildFlyerHtml } from "../../utils/flyerDesarrolladoras";
import { descargarFlyerPdf, descargarFlyerImagen } from "../../utils/flyerExport";
import { useMaterialesData } from "../../hooks/useMaterialesData";
import DeckEditor from "./DeckEditor";
import BloquesEditor from "./BloquesEditor";
import GuiaViewer from "./GuiaViewer";
import MaterialPersonalViewer from "./MaterialPersonalViewer";
import MaterialContactoDev, { TIPO_CONTACTO_DEV } from "./MaterialContactoDev";
import ManualesVideoViewer from "./ManualesVideoViewer";
import { TIPO_VIDEO_MANUALES } from "../../utils/manualesVideos";
import { TIPO_DECK, DEFAULT_DECK } from "../../utils/deckQueEs";
import { TIPO_GUIA, GUIAS } from "../../utils/guiasHabitia";
import { TIPO_PERSONAL, PERSONALES } from "../../utils/materialesPersonales";
import {
  TIPO_GUION, TIPO_OBJECIONES, BLOQUES_CONFIG, BUILTIN_BLOQUES, CANALES, canalDeMaterial, bloqueVacio,
} from "../../utils/materialesBloques";
import {
  LS_CONTACTO, loadContacto, Label, SecTitle, input, chkLabel, topbarStyle, tabsRow, tabBtn,
  backBtn, primaryBtn, ghostBtn, miniBtn, iconBtn, descMenu, descItem, infoBanner,
  boxDest, boxContacto, boxPuntos,
} from "./materialesUi";
import {
  FaRegFileAlt, FaDownload, FaRegCopy, FaPlus, FaTrash, FaArrowUp, FaArrowDown,
  FaArrowLeft, FaRegClone, FaSave, FaPen, FaEye, FaChevronDown, FaRegFilePdf, FaRegImage,
  FaRegImages, FaCode, FaRegComments, FaRegLightbulb, FaWhatsapp, FaRegEnvelope,
  FaWalking, FaClipboardList, FaSearch, FaBookOpen, FaChevronRight, FaRegUser,
  FaRegAddressCard, FaVideo,
} from "react-icons/fa";

const CANAL_ICONS = { whatsapp: FaWhatsapp, email: FaRegEnvelope, presencial: FaWalking, interno: FaClipboardList };

// Deck "Qué es Habitia": plantilla definida en código. Se muestra siempre en
// "Materiales generales"; si un superadmin guarda su versión general en
// Firestore, ese doc la reemplaza (y si lo borra, vuelve esta).
const BUILTIN_DECK = {
  id: "builtin-deck-que-es", builtin: true, nombre: "Deck — Qué es Habitia",
  tipo: TIPO_DECK, scope: "general", deck: DEFAULT_DECK,
};
// Playbook comercial (guiones/objeciones por canal): plantillas en código, mismo
// modelo que el deck — el doc general con la misma plantillaId las reemplaza.
const BUILTIN_MATERIALES = BUILTIN_BLOQUES.map((b) => ({
  id: `builtin-${b.plantillaId}`, builtin: true, plantillaId: b.plantillaId,
  nombre: b.nombre, tipo: b.tipo, canal: b.canal, scope: "general", bloques: b.bloques,
}));
// Guías públicas de habitia.ai (solo lectura: se comparten por link o PDF con
// el diseño de la web). Siempre builtin — el contenido canónico vive en la landing.
const BUILTIN_GUIAS = GUIAS.map((g) => ({
  id: `builtin-guia${g.path.replace(/\//g, "-")}`, builtin: true,
  nombre: g.navLabel, tipo: TIPO_GUIA, canal: "email", guiaPath: g.path, scope: "general",
}));
// Materiales personales: cartas A/B/C, tarjeta con QR y firma de mail. Se
// completan SOLOS con los datos del usuario logueado (nombre/celular/email del
// doc users) — no se editan, se imprimen o copian.
const BUILTIN_PERSONALES = PERSONALES.map((p) => ({
  id: `builtin-personal-${p.subtipo}`, builtin: true,
  nombre: p.nombre, tipo: TIPO_PERSONAL, subtipo: p.subtipo, canal: p.canal,
  detallePersonal: p.detalle, scope: "general",
}));
// PDFs ya diseñados, servidos desde /public. Se abren en pestaña nueva para
// ver / descargar / compartir — no tienen editor. Ej.: el PDF de planes que se
// le manda a las administraciones (canal B2B).
const BUILTIN_PDFS = [{
  id: "builtin-pdf-planes-adm", builtin: true,
  nombre: "Planes para Administraciones (PDF)",
  tipo: "pdf_estatico", canal: "email", scope: "general",
  pdfUrl: "/materiales/planes-administraciones.pdf",
}];
// Primer contacto con desarrolladoras (canal B2B): abre un panel donde el comercial
// escribe el nombre del estudio y arma el mensaje (WhatsApp o mail) con su firma +
// el folleto para adjuntar, listo para copiar/enviar. Textos en plantillasDesarrolladoras.
const BUILTIN_CONTACTO_DEV = [{
  id: "builtin-contacto-desarrolladoras", builtin: true,
  nombre: "Primer contacto — Desarrolladoras",
  tipo: TIPO_CONTACTO_DEV, canal: "whatsapp", scope: "general",
}];
// Manuales en video para residentes (contenido de habitia.ai). Se MUESTRAN en
// reuniones o se comparte el link por WhatsApp — no se editan. Builtin: el
// contenido canónico vive en la landing.
const BUILTIN_VIDEOS = [{
  id: "builtin-video-manuales", builtin: true,
  nombre: "Manuales en video (residentes)",
  tipo: TIPO_VIDEO_MANUALES, canal: "presencial", scope: "general",
}];

const clone = (x) => JSON.parse(JSON.stringify(x));

/**
 * Materiales — biblioteca de folletos/flyers comerciales. Galería de materiales
 * (colección `materiales`, compartida) + editor con preview dominante (mismo
 * layout que Presupuestos). Cada material es editable, se guarda y se descarga
 * para email o WhatsApp.
 */
export default function Materiales() {
  const isMobile = useIsMobile();
  const { userType, userEmail } = useUser();
  const isSuperAdmin = Number(userType) === 2;
  const myEmail = String(userEmail || auth.currentUser?.email || "").toLowerCase();
  const { materiales, loading, error, upsertLocal, removeLocal } = useMaterialesData();
  const [abierto, setAbierto] = useState(null); // material en edición (id null = nuevo)
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [busca, setBusca] = useState("");
  const [filtroCanal, setFiltroCanal] = useState("");
  const [soloMios, setSoloMios] = useState(false);

  // Generales = plantillas de Habitia (las ven todos). Míos = personales del asesor.
  const generales = materiales.filter((m) => (m.scope || "general") !== "personal");
  const mios = materiales.filter((m) => m.scope === "personal" && String(m.owner || "").toLowerCase() === myEmail);
  const hayDeckGeneral = generales.some((m) => m.tipo === TIPO_DECK);
  const generalesVista = [
    ...(hayDeckGeneral ? [] : [BUILTIN_DECK]),
    ...BUILTIN_MATERIALES.filter((b) => !generales.some((m) => m.plantillaId === b.plantillaId)),
    ...BUILTIN_GUIAS,
    ...BUILTIN_PDFS,
    ...BUILTIN_CONTACTO_DEV,
    ...BUILTIN_VIDEOS,
    ...BUILTIN_PERSONALES,
    ...generales,
  ];
  // La galería se agrupa por canal (qué va por WhatsApp / mail / presencial / interno).
  const todos = [...generalesVista, ...mios];
  const visibles = todos.filter((m) =>
    (!soloMios || (m.scope || "general") === "personal")
    && (!busca.trim() || (m.nombre || "").toLowerCase().includes(busca.trim().toLowerCase()))
  );

  const nuevoFlyer = () => {
    setNuevoOpen(false);
    setAbierto({
      id: null, nombre: "", tipo: "flyer_desarrolladoras", puntos: DEFAULT_PUNTOS.map((p) => ({ ...p })),
      scope: isSuperAdmin ? "general" : "personal", owner: isSuperAdmin ? "" : myEmail,
    });
  };
  const nuevoDeck = () => {
    setNuevoOpen(false);
    setAbierto({
      id: null, nombre: "", tipo: TIPO_DECK, deck: clone(DEFAULT_DECK),
      scope: isSuperAdmin ? "general" : "personal", owner: isSuperAdmin ? "" : myEmail,
    });
  };
  const nuevoBloques = (tipo) => {
    setNuevoOpen(false);
    setAbierto({
      id: null, nombre: "", tipo, bloques: bloqueVacio(),
      canal: tipo === TIPO_OBJECIONES ? "interno" : "whatsapp",
      scope: isSuperAdmin ? "general" : "personal", owner: isSuperAdmin ? "" : myEmail,
    });
  };
  const duplicar = (m) => {
    if (m.tipo === TIPO_DECK) {
      setAbierto({
        id: null, nombre: `${m.nombre || "Deck"} (copia)`, tipo: TIPO_DECK,
        deck: clone(m.deck || DEFAULT_DECK), scope: "personal", owner: myEmail,
      });
      return;
    }
    if (m.tipo === TIPO_GUION || m.tipo === TIPO_OBJECIONES) {
      setAbierto({
        id: null, nombre: `${m.nombre || BLOQUES_CONFIG[m.tipo].nombre} (copia)`, tipo: m.tipo,
        bloques: clone(m.bloques || bloqueVacio()), canal: canalDeMaterial(m),
        scope: "personal", owner: myEmail,
      });
      return;
    }
    setAbierto({
      id: null, nombre: `${m.nombre || "Material"} (copia)`, tipo: m.tipo || "flyer_desarrolladoras",
      puntos: (m.puntos || DEFAULT_PUNTOS).map((p) => ({ ...p })), scope: "personal", owner: myEmail,
    });
  };

  // PDF estático: se abre en pestaña nueva (ver/descargar), no tiene editor.
  const abrirMaterial = (m) => {
    if (m.tipo === "pdf_estatico") { window.open(m.pdfUrl, "_blank", "noopener"); return; }
    setAbierto(m);
  };

  if (abierto) {
    const editorProps = {
      material: abierto, isMobile, isSuperAdmin, myEmail,
      onClose: () => setAbierto(null),
      onSaved: (id, data) => { upsertLocal(id, data); setAbierto({ id, ...data }); },
      onDeleted: (id) => { removeLocal(id); setAbierto(null); },
    };
    if (abierto.tipo === TIPO_GUIA) return <GuiaViewer material={abierto} isMobile={isMobile} onClose={() => setAbierto(null)} />;
    if (abierto.tipo === TIPO_PERSONAL) return <MaterialPersonalViewer material={abierto} isMobile={isMobile} onClose={() => setAbierto(null)} />;
    if (abierto.tipo === TIPO_CONTACTO_DEV) return <MaterialContactoDev material={abierto} isMobile={isMobile} onClose={() => setAbierto(null)} />;
    if (abierto.tipo === TIPO_VIDEO_MANUALES) return <ManualesVideoViewer isMobile={isMobile} onClose={() => setAbierto(null)} />;
    if (abierto.tipo === TIPO_DECK) return <DeckEditor {...editorProps} />;
    if (abierto.tipo === TIPO_GUION || abierto.tipo === TIPO_OBJECIONES) return <BloquesEditor {...editorProps} />;
    return <MaterialEditor {...editorProps} />;
  }

  return (
    <div style={{ padding: isMobile ? 16 : spacing[6], maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: spacing[3], position: "relative" }}>
        <button onClick={() => setNuevoOpen((o) => !o)} style={primaryBtn} aria-haspopup="menu" aria-expanded={nuevoOpen}>
          <FaPlus size={12} /> Nuevo material <FaChevronDown size={9} />
        </button>
        {nuevoOpen && (
          <>
            <div onClick={() => setNuevoOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
            <div role="menu" style={descMenu}>
              <button onClick={nuevoFlyer} style={descItem}><FaRegFileAlt size={12} color={colors.primary} /> Folleto desarrolladoras</button>
              <button onClick={nuevoDeck} style={descItem}><FaRegImages size={12} color={colors.primary} /> Deck — Qué es Habitia</button>
              <button onClick={() => nuevoBloques(TIPO_GUION)} style={descItem}><FaRegComments size={12} color={colors.primary} /> Guion comercial</button>
              <button onClick={() => nuevoBloques(TIPO_OBJECIONES)} style={descItem}><FaRegLightbulb size={12} color={colors.primary} /> Banco de objeciones</button>
            </div>
          </>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: spacing[8], color: colors.gray600 }}>Cargando materiales…</div>
      ) : error ? (
        <div style={{ ...cardBase, padding: spacing[5], color: colors.error, fontSize: typography.sizes.sm, textAlign: "center" }}>
          No se pudieron cargar los materiales. Si recién se publicaron las reglas de Firestore, recargá la página; si no, es un tema de permisos.
        </div>
      ) : (
        <>
          {/* Buscador + filtro por canal */}
          <div style={{ display: "flex", gap: spacing[2], alignItems: "center", flexWrap: "wrap", marginBottom: spacing[5] }}>
            <div style={{ display: "flex", alignItems: "center", gap: spacing[2], flex: "1 1 220px", minWidth: 200, background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: "0 10px" }}>
              <FaSearch size={12} color={colors.gray400} />
              <input
                value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar material…"
                aria-label="Buscar material"
                style={{ flex: 1, minWidth: 0, border: "none", outline: "none", padding: "10px 0", fontSize: typography.sizes.base, fontFamily: typography.fontFamily, background: "transparent", color: colors.gray900 }}
              />
            </div>
            <ChipCanal activo={filtroCanal === "" && !soloMios} onClick={() => { setFiltroCanal(""); setSoloMios(false); }} label="Todos" />
            {CANALES.map((c) => (
              <ChipCanal key={c.id} activo={filtroCanal === c.id} onClick={() => setFiltroCanal(filtroCanal === c.id ? "" : c.id)} label={c.label} icon={CANAL_ICONS[c.id]} />
            ))}
            <ChipCanal activo={soloMios} onClick={() => setSoloMios((v) => !v)} label="Míos" icon={FaRegUser} />
          </div>

          {CANALES.filter((c) => !filtroCanal || filtroCanal === c.id).map((c) => {
            const items = visibles.filter((m) => canalDeMaterial(m) === c.id);
            if (items.length === 0) return null;
            return <SeccionCanal key={c.id} canal={c} items={items} onOpen={abrirMaterial} onDup={duplicar} />;
          })}
          {visibles.length === 0 && (
            <div style={{ ...cardBase, padding: spacing[5], textAlign: "center", color: colors.gray600, fontSize: typography.sizes.sm }}>
              {soloMios
                ? "Todavía no guardaste materiales tuyos. Abrí un material general, personalizalo y guardalo — se crea tu copia acá."
                : "No hay materiales que coincidan con la búsqueda."}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ChipCanal({ activo, onClick, label, icon: Icon }) {
  return (
    <button
      onClick={onClick} aria-pressed={activo}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px",
        border: `1px solid ${activo ? colors.primary : colors.gray200}`, borderRadius: radius.full,
        background: activo ? colors.primaryLighter : colors.white, color: activo ? colors.primary : colors.gray700,
        cursor: "pointer", fontSize: typography.sizes.sm, fontFamily: "inherit", transition: "all 0.15s ease",
      }}
    >
      {Icon && <Icon size={11} />} {label}
    </button>
  );
}

/**
 * Sección por canal como lista de filas-link (patrón de bibliotecas de
 * contenido tipo LinkedIn Learning / Microsoft Learn / Notion): una card
 * contenedora por canal, filas compactas escaneables, detalle recién al abrir.
 */
function SeccionCanal({ canal, items, onOpen, onDup }) {
  const Icon = CANAL_ICONS[canal.id] || FaRegFileAlt;
  return (
    <div style={{ ...cardBase, padding: 0, overflow: "hidden", marginBottom: spacing[4] }}>
      {/* Encabezado de categoría: azul sobre fondo suave, para diferenciarse de las filas */}
      <div style={{ display: "flex", alignItems: "center", gap: spacing[3], padding: `${spacing[3]}px ${spacing[4]}px`, background: colors.primarySubtle, borderBottom: `1px solid ${colors.gray100}` }}>
        <div style={{ width: 30, height: 30, borderRadius: radius.sm, background: colors.primaryLighter, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={13} color={colors.primary} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: typography.sizes.lg, color: colors.primary }}>{canal.label}</span>
          <span style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}> · {items.length}</span>
          <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>{canal.desc}</div>
        </div>
      </div>
      <div>
        {items.map((m, i) => (
          <MaterialRow key={m.id} m={m} ultimo={i === items.length - 1} onOpen={onOpen} onDup={onDup} />
        ))}
      </div>
    </div>
  );
}

function MaterialRow({ m, ultimo, onOpen, onDup }) {
  const esMio = (m.scope || "general") === "personal";
  const esDeck = m.tipo === TIPO_DECK;
  const esGuia = m.tipo === TIPO_GUIA;
  const esPdf = m.tipo === "pdf_estatico";
  const esPersonal = m.tipo === TIPO_PERSONAL;
  const esBloques = m.tipo === TIPO_GUION || m.tipo === TIPO_OBJECIONES;
  const esContactoDev = m.tipo === TIPO_CONTACTO_DEV;
  const esVideos = m.tipo === TIPO_VIDEO_MANUALES;
  const Icono = esVideos ? FaVideo : esContactoDev ? FaWhatsapp : esPersonal ? FaRegAddressCard : esPdf ? FaRegFilePdf : esDeck ? FaRegImages : esGuia ? FaBookOpen : m.tipo === TIPO_GUION ? FaRegComments : m.tipo === TIPO_OBJECIONES ? FaRegLightbulb : FaRegFileAlt;
  const detalle = esVideos
    ? "7 videos cortos narrados por Habi · para mostrar o compartir por WhatsApp"
    : esContactoDev
    ? "Mensaje (WhatsApp o mail) + folleto para adjuntar · con tu firma"
    : esPersonal
    ? m.detallePersonal || "Se completa con tus datos"
    : esPdf
      ? "PDF · planes del canal B2B (para administradoras)"
      : esDeck
        ? "Presentación · 3 slides"
        : esGuia
          ? `Guía de la web · habitia.ai${m.guiaPath}`
          : esBloques
            ? BLOQUES_CONFIG[m.tipo].detalle((m.bloques || []).filter((b) => b.incluido && ((b.titulo || "").trim() || (b.cuerpo || "").trim())).length)
            : `Folleto · ${(m.puntos || []).filter((p) => p.incluido && (p.titulo || "").trim()).length} puntos`;
  return (
    <div
      className="habitia-table-row"
      onClick={() => onOpen(m)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen(m)}
      style={{
        display: "flex", alignItems: "center", gap: spacing[3],
        padding: `${spacing[2]}px ${spacing[4]}px`, minHeight: 52, cursor: "pointer",
        borderBottom: ultimo ? "none" : `1px solid ${colors.gray100}`,
        transition: "background 0.15s ease",
      }}
    >
      <div style={{ width: 32, height: 32, borderRadius: radius.sm, background: colors.primaryLighter, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icono size={14} color={colors.primary} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2], minWidth: 0 }}>
          <span style={{ fontSize: typography.sizes.md, color: colors.gray900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {m.nombre || "Sin nombre"}
          </span>
          {esMio && (
            <span style={{ flexShrink: 0, fontSize: typography.sizes.xs, color: colors.successDark, background: colors.successLight, padding: "1px 8px", borderRadius: radius.full }}>Mío</span>
          )}
        </div>
        <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{detalle}</div>
      </div>
      {!esGuia && !esPdf && !esPersonal && !esContactoDev && !esVideos && (
        <button
          onClick={(e) => { e.stopPropagation(); onDup(m); }}
          aria-label={`Duplicar ${m.nombre || "material"}`} title="Duplicar"
          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, border: "none", borderRadius: radius.sm, background: "transparent", color: colors.gray400, cursor: "pointer", flexShrink: 0, transition: "all 0.15s ease" }}
        >
          <FaRegClone size={12} />
        </button>
      )}
      <FaChevronRight size={11} color={colors.gray300} style={{ flexShrink: 0 }} />
    </div>
  );
}

function MaterialEditor({ material, isMobile, isSuperAdmin, myEmail, onClose, onSaved, onDeleted }) {
  const { showToast } = useToast();
  const { userName, userEmail } = useUser();
  const email = userEmail || auth.currentUser?.email || "";

  const esGeneral = (material.scope || "general") !== "personal";
  const copiaAMios = esGeneral && !isSuperAdmin && !!material.id; // comercial editando una general → copia
  const puedeEliminar = !!material.id && (isSuperAdmin || !esGeneral);

  const c0 = loadContacto(email);
  const [nombre, setNombre] = useState(material.nombre || "");
  const [puntos, setPuntos] = useState((material.puntos || DEFAULT_PUNTOS).map((p) => ({ ...p })));
  const [destinatario, setDestinatario] = useState("");
  const [agenteName, setAgenteName] = useState(c0.name || userName || "");
  const [agenteEmail, setAgenteEmail] = useState(c0.email || email);
  const [agenteTel, setAgenteTel] = useState(c0.tel || "");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("editar"); // mobile: editar | preview
  const [descOpen, setDescOpen] = useState(false);
  const [exportando, setExportando] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => { if (userName && !agenteName) setAgenteName(userName); }, [userName]); // eslint-disable-line
  useEffect(() => { if (email && !agenteEmail) setAgenteEmail(email); }, [email]); // eslint-disable-line
  useEffect(() => {
    try { localStorage.setItem(LS_CONTACTO(email), JSON.stringify({ name: agenteName, email: agenteEmail, tel: agenteTel })); } catch (_) {}
  }, [agenteName, agenteEmail, agenteTel, email]);

  const html = useMemo(
    () => buildFlyerHtml({ destinatario, puntos, agente: { name: agenteName, email: agenteEmail, tel: agenteTel, cargo: "Agente Comercial" } }),
    [destinatario, puntos, agenteName, agenteEmail, agenteTel]
  );
  const [previewHtml, setPreviewHtml] = useState(html);
  useEffect(() => { const t = setTimeout(() => setPreviewHtml(html), 300); return () => clearTimeout(t); }, [html]);

  const fitIframe = () => {
    try {
      const h = iframeRef.current?.contentDocument?.body?.scrollHeight;
      if (h) iframeRef.current.style.height = `${h + 24}px`;
    } catch (_) {}
  };

  // ── Operaciones sobre puntos ──
  const upd = (id, patch) => setPuntos((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const del = (id) => setPuntos((ps) => ps.filter((p) => p.id !== id));
  const move = (id, dir) => setPuntos((ps) => {
    const i = ps.findIndex((p) => p.id === id); const j = i + dir;
    if (i < 0 || j < 0 || j >= ps.length) return ps;
    const next = ps.slice(); [next[i], next[j]] = [next[j], next[i]]; return next;
  });
  const add = () => setPuntos((ps) => [...ps, { id: `p${Date.now()}`, titulo: "", desc: "", incluido: true }]);
  const resetPuntos = () => setPuntos(DEFAULT_PUNTOS.map((p) => ({ ...p })));

  async function guardar() {
    if (!nombre.trim()) { showToast("Poné un nombre al material.", "error"); return; }
    setSaving(true);
    try {
      // Un comercial que edita una general genera SU copia en Mis materiales.
      let scope, owner, id;
      if (copiaAMios) { scope = "personal"; owner = myEmail; id = null; }
      else if (material.id) {
        scope = material.scope || (esGeneral ? "general" : "personal");
        owner = scope === "personal" ? (material.owner || myEmail) : "";
        id = material.id;
      } else {
        scope = material.scope || (isSuperAdmin ? "general" : "personal");
        owner = scope === "personal" ? myEmail : "";
        id = null;
      }
      const payload = { nombre: nombre.trim(), tipo: material.tipo || "flyer_desarrolladoras", puntos, scope, owner, updatedAt: serverTimestamp(), updatedBy: email };
      if (!id) {
        payload.createdAt = serverTimestamp();
        payload.createdBy = email;
        const ref = await addDoc(collection(db, "materiales"), payload);
        id = ref.id;
      } else {
        await setDoc(doc(db, "materiales", id), payload, { merge: true });
      }
      showToast(copiaAMios ? "Guardado en Mis materiales." : "Material guardado.", "success");
      onSaved(id, { ...payload });
    } catch (e) {
      showToast(e.message || "No se pudo guardar.", "error");
    }
    setSaving(false);
  }

  async function eliminar() {
    if (!material.id) { onClose(); return; }
    // eslint-disable-next-line no-alert
    if (!window.confirm("¿Eliminar este material?")) return;
    try { await deleteDoc(doc(db, "materiales", material.id)); showToast("Material eliminado.", "success"); onDeleted(material.id); }
    catch (e) { showToast(e.message || "No se pudo eliminar.", "error"); }
  }

  const slugName = () => (destinatario || nombre || "flyer").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "flyer";

  function descargarHtml() {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `habitia-${slugName()}.html`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast("Flyer HTML descargado.", "success");
  }
  async function doExport(tipo) {
    setDescOpen(false);
    if (tipo === "html") return descargarHtml();
    setExportando(true);
    try {
      const fn = `habitia-${slugName()}`;
      if (tipo === "pdf") await descargarFlyerPdf(html, `${fn}.pdf`);
      else await descargarFlyerImagen(html, `${fn}.png`);
      showToast("Descargado. Listo para email o WhatsApp.", "success");
    } catch (e) {
      showToast("No se pudo generar el archivo. Probá de nuevo.", "error");
    }
    setExportando(false);
  }
  async function copiar() {
    try { await navigator.clipboard.writeText(html); showToast("HTML copiado.", "success"); }
    catch { showToast("No se pudo copiar.", "error"); }
  }

  const incluidos = puntos.filter((p) => p.incluido && (p.titulo || "").trim()).length;

  const descargarBtn = (
    <div style={{ position: "relative" }}>
      <button onClick={() => setDescOpen((o) => !o)} disabled={exportando} style={ghostBtn} aria-haspopup="menu" aria-expanded={descOpen}>
        <FaDownload size={12} /> {exportando ? "Generando…" : "Descargar"} <FaChevronDown size={9} />
      </button>
      {descOpen && (
        <>
          <div onClick={() => setDescOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div role="menu" style={descMenu}>
            <button onClick={() => doExport("pdf")} style={descItem}><FaRegFilePdf size={12} color={colors.error} /> PDF</button>
            <button onClick={() => doExport("img")} style={descItem}><FaRegImage size={12} color={colors.primary} /> Imagen (PNG)</button>
            <button onClick={() => doExport("html")} style={descItem}><FaCode size={12} color={colors.gray600} /> HTML</button>
          </div>
        </>
      )}
    </div>
  );

  const editorPanel = (
    <div style={{ display: "grid", gap: spacing[3] }}>
      {copiaAMios && (
        <div style={infoBanner}>Material <b>general</b> de Habitia. Al guardar se crea <b>tu copia</b> en “Mis materiales” (renombrala como quieras).</div>
      )}

      <section style={boxDest}>
        <SecTitle color={colors.primary}>Destinatario</SecTitle>
        <input value={destinatario} onChange={(e) => setDestinatario(e.target.value)} placeholder="Ej. Grupo Ferere" style={input} />
      </section>

      <section style={boxContacto}>
        <SecTitle color={colors.gray700}>Tu contacto — Agente Comercial</SecTitle>
        <Label>Nombre</Label>
        <input value={agenteName} onChange={(e) => setAgenteName(e.target.value)} placeholder="Nombre y apellido" style={input} />
        <div style={{ height: spacing[2] }} />
        <Label>Email</Label>
        <input value={agenteEmail} onChange={(e) => setAgenteEmail(e.target.value)} placeholder="vos@habitia.ai" style={input} />
        <div style={{ height: spacing[2] }} />
        <Label>Teléfono</Label>
        <input value={agenteTel} onChange={(e) => setAgenteTel(e.target.value)} placeholder="+598 9x xxx xxx" style={input} />
        <p style={{ margin: `6px 0 0`, fontSize: typography.sizes.xs, color: colors.gray600 }}>Figurás como <b>Agente Comercial</b> con estos datos.</p>
      </section>

      <section style={boxPuntos}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: spacing[2] }}>
          <SecTitle color={colors.primary} style={{ marginBottom: 0 }}>Puntos del proyecto ({incluidos})</SecTitle>
          <button onClick={resetPuntos} style={miniBtn}>Restaurar</button>
        </div>
        <div style={{ display: "grid", gap: spacing[2] }}>
          {puntos.map((p, i) => (
            <div key={p.id} style={{ border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: spacing[2], background: p.incluido ? colors.white : colors.gray50 }}>
              <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: 6, flexWrap: "wrap" }}>
                <label style={chkLabel}><input type="checkbox" checked={!!p.incluido} onChange={(e) => upd(p.id, { incluido: e.target.checked })} /> Incluir</label>
                <label style={chkLabel}><input type="checkbox" checked={!!p.proximamente} onChange={(e) => upd(p.id, { proximamente: e.target.checked })} /> Próx.</label>
                <div style={{ flex: 1 }} />
                <button onClick={() => move(p.id, -1)} disabled={i === 0} style={iconBtn} aria-label="Subir"><FaArrowUp size={10} /></button>
                <button onClick={() => move(p.id, 1)} disabled={i === puntos.length - 1} style={iconBtn} aria-label="Bajar"><FaArrowDown size={10} /></button>
                <button onClick={() => del(p.id)} style={{ ...iconBtn, color: colors.error }} aria-label="Borrar"><FaTrash size={10} /></button>
              </div>
              <input value={p.titulo} onChange={(e) => upd(p.id, { titulo: e.target.value })} placeholder="Título" style={{ ...input, marginBottom: 6 }} />
              <textarea value={p.desc} onChange={(e) => upd(p.id, { desc: e.target.value })} placeholder="Descripción" rows={2} style={{ ...input, resize: "vertical", fontFamily: typography.fontFamily }} />
            </div>
          ))}
        </div>
        <button onClick={add} style={{ ...miniBtn, marginTop: spacing[2], width: "100%", justifyContent: "center" }}><FaPlus size={10} /> Agregar punto</button>
      </section>
    </div>
  );

  const previewPane = (
    <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto", background: "#E9EBEE", padding: isMobile ? 10 : spacing[4] }}>
      <iframe ref={iframeRef} title="Vista previa" srcDoc={previewHtml} onLoad={fitIframe} scrolling="no"
        style={{ width: "100%", border: 0, display: "block", background: "#ececec" }} />
    </div>
  );

  const topbar = (
    <div style={topbarStyle}>
      <button onClick={onClose} style={backBtn} aria-label="Volver a materiales"><FaArrowLeft size={12} /> {isMobile ? "" : "Materiales"}</button>
      <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre del material" style={{ ...input, flex: 1, minWidth: 0, height: 38, background: colors.gray50 }} />
      <button onClick={guardar} disabled={saving} style={primaryBtn}><FaSave size={12} /> {saving ? "…" : (copiaAMios ? "Guardar copia" : "Guardar")}</button>
      {!isMobile && descargarBtn}
      {!isMobile && <button onClick={copiar} style={ghostBtn}><FaRegCopy size={12} /> Copiar</button>}
      {!isMobile && puedeEliminar && <button onClick={eliminar} style={{ ...ghostBtn, color: colors.error }} aria-label="Eliminar"><FaTrash size={12} /></button>}
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        {topbar}
        <div style={tabsRow}>
          <button onClick={() => setTab("editar")} style={tabBtn(tab === "editar")}><FaPen size={11} /> Editar</button>
          <button onClick={() => setTab("preview")} style={tabBtn(tab === "preview")}><FaEye size={11} /> Vista previa</button>
        </div>
        {tab === "editar" ? (
          <div style={{ padding: 14, overflowY: "auto" }}>
            {editorPanel}
            <div style={{ display: "flex", gap: spacing[2], marginTop: spacing[4], alignItems: "center" }}>
              {descargarBtn}
              <button onClick={copiar} style={ghostBtn}><FaRegCopy size={12} /></button>
              {puedeEliminar && <button onClick={eliminar} style={{ ...ghostBtn, color: colors.error }}><FaTrash size={12} /></button>}
            </div>
          </div>
        ) : (
          previewPane
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {topbar}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {previewPane}
        <aside style={{ width: 360, flexShrink: 0, overflowY: "auto", borderLeft: `1px solid ${colors.gray200}`, background: colors.white, padding: spacing[4] }}>
          {editorPanel}
        </aside>
      </div>
    </div>
  );
}

// Estilos y helpers compartidos con DeckEditor viven en ./materialesUi.js
